import Table from "@shared/components/Table"
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { getBankAccounts, createBankAccount, deleteBankAccount, updateBankAccount } from "@modules/payments/companyBankAcc/services/bankAccounts";
import BankAccountsForm from "./BankAccountsForm";
import { notifyError, notifySuccess } from '@config/tostifyConfig/tostifyConfig';
import DeleteModal from "@shared/components/DeleteModal";

export default function BankAccounts() {
    const [bankAccounts, setBankAccounts] = useState([]);
    const [selectedBankAccount, setSelectedBankAccount] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [modalState, setModalState] = useState({ visible: false, type: null });
    const [isCreating, setIsCreating] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const adminBranchId = useSelector((state) => state.auth.adminBranchId);
    
    // Check if we're in selection mode from navigation state
    const selectionMode = location.state?.selectionMode || false;
    const returnPath = location.state?.returnPath;
    const fieldName = location.state?.fieldName;
    const preservedState = location.state?.preservedState;
    
    const baseColumns = [
        { key: "banks.bankCode", label: "BIC", render: (row) => row.banks?.bankCode || '-' },
        { key: "iban", label: "IBAN" },
        { key: "currency.mnemonic", label: t('payments.bankAccounts.form.currency'), render: (row) => row.currency?.mnemonic || '-' },
        { key: "note", label: t('payments.bankAccounts.form.purpose') },
        { key: "banks.name", label: t('payments.bankAccounts.form.bank'), render: (row) => row.banks?.name || '-' },
        { key: "accounts", label: t('payments.bankAccounts.form.account'), render: (row) => row.accounts || '-' },
        { key: "paymentsInFolder", label: t('payments.bankAccounts.form.paymentFolder'), render: (row) => row.paymentsInFolder || '-' },
    ];

    const handleConfirmSelection = (bankAccount) => {
        
        if (!returnPath) {
            console.error('❌ ERROR: returnPath is undefined! Cannot navigate back.');
            notifyError('Navigation error: Return path not found');
            return;
        }
        
        navigate(returnPath, {
            replace: true,
            state: {
                selectedBankAccount: bankAccount,
                fieldName: fieldName,
                preservedState: preservedState
            }
        });
    };

    const selectColumn = {
        key: "select",
        label: t('common.chooseBankAccount') || "Action",
        render: (row) => (
            <button
                className="form-btn btn-green"
                onClick={() => handleConfirmSelection(row)}
                style={{ padding: '5px 15px', fontSize: '14px' }}
            >
                {t('common.select') || 'Select'}
            </button>
        )
    };

    const columns = selectionMode ? [...baseColumns, selectColumn] : baseColumns;


    const actionButtons = (
        <>
            <button
                className="icon-button"
                onClick={() => handleOpenModal('add')}
                title={t('common.add')}
            >
                <FontAwesomeIcon icon={faPlus} />
            </button>
            <button
                className={`icon-button ${!selectedRecord ? 'disabled-link' : ''}`}
                disabled={!selectedRecord}
                onClick={() => handleOpenModal('edit')}
                title={t('common.edit')}
            >
                <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
                className={`icon-button ${!selectedRecord ? 'disabled-link' : ''}`}
                disabled={!selectedRecord}
                onClick={() => handleOpenModal('delete')}
                title={t('common.delete')}
            >
                <FontAwesomeIcon icon={faTrash} />
            </button>
        </>
    );

    const handleOpenModal = (type) => {
        setModalState({ visible: true, type });
    };

    const handleCloseModal = () => {
        setModalState({ visible: false, type: null });
        setSelectedRecord(null);
        setSelectedBankAccount(null);
    };

    const handleSelectBankAccount = (bankAccount) => {
        if (selectedRecord && selectedRecord.id === bankAccount.id) {
            setSelectedBankAccount(null);
            setSelectedRecord(null);
        } else {
            setSelectedBankAccount(bankAccount.id);
            setSelectedRecord(bankAccount);
        }
    };

    const handleCheckboxChange = (index) => {
        const bankAccount = bankAccounts[index];
        handleSelectBankAccount(bankAccount);
    };

    const handleConfirm = async (formData) => {
        try {
            if (modalState.type === 'add') {
                const createData = {
                    empBranchId: formData.bankId,
                    bankId: formData.bankId,
                    currencyId: formData.currencyId,
                    iban: formData.iban,
                    active: formData.active === true,
                    note: formData.purpose || ''
                };
                
                await createBankAccount(createData);
                notifySuccess(t('common.createdSuccessfully'));
            } else if (modalState.type === 'edit' && selectedRecord) {
                const updateData = {
                    empBranchId: formData.bankId,
                    bankId: formData.bankId,
                    currencyId: formData.currencyId,
                    iban: formData.iban,
                    active: formData.active === true,
                    note: formData.purpose || ''
                };
                
                await updateBankAccount(selectedRecord.id, updateData);
                notifySuccess(t('common.updatedSuccessfully'));
            }
            handleCloseModal();
            // Refresh the list
            const firmId = adminBranchId || 1;
            const bankAccounts = await getBankAccounts(firmId);
            setBankAccounts(bankAccounts);
        } catch (error) {
            const errorMessage = error.response?.data || t('common.errorMessage');
            console.error('Error saving record:', error);
            notifyError(errorMessage || t('common.errorMessage'));
        }
    }

    const handleDeleteConfirm = async () => {
        try {
            await deleteBankAccount(selectedRecord.id);
            notifySuccess(t('common.deletedSuccessfully'));
            await fetchBankAccounts();
            handleCloseModal();
        } catch (error) {
            console.error('Error deleting record:', error);
            notifyError(t('common.errorMessage'));
        }
    };

    const fetchBankAccounts = async () => {
        const firmId = adminBranchId || 1;
        const bankAccounts = await getBankAccounts(firmId);
        setBankAccounts(bankAccounts);
    };

    useEffect(() => {
        if (adminBranchId) {
            fetchBankAccounts();
        }
    }, [adminBranchId]);

    return (
        <>
            <Table
                title={t('payments.bankAccounts.title')}
                columns={columns}
                actionButtons={actionButtons}
                showCustomSearch={true}
                customSearchComponent={null}
                showPagination={false}
                showPageSizeSelector={false}
                data={bankAccounts}
                selectedData={selectedRecord?.id}
                handleCheckboxChange={handleCheckboxChange}
            />
            {modalState.visible && (modalState.type === 'add' || modalState.type === 'edit') && (
                <BankAccountsForm
                    onClose={handleCloseModal}
                    onConfirm={handleConfirm}
                    editData={modalState.type === 'edit' ? selectedRecord : null}
                />
            )}
            {modalState.visible && modalState.type === 'delete' && selectedRecord && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleDeleteConfirm}
                    name={selectedRecord.name}
                    entityType="record"
                />
            )}
        </>
    );
}

